import React, { useState, useEffect } from "react";
import { DatePicker, Select, Button, message } from "antd";
import dayjs from "dayjs";
import axiosInstance from "../../../services/axiosInstance";

const { RangePicker } = DatePicker;

const DATE_PRESETS = {
  this_week: "this_week",
  today: "today",
  this_month: "this_month",
  custom: "custom",
};

const getDateRangeFromPreset = (preset) => {
  const today = new Date();
  
  switch (preset) {
    case DATE_PRESETS.today:
      return { from: today, to: today };
    case DATE_PRESETS.this_week: {
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { from: monday, to: sunday };
    }
    case DATE_PRESETS.this_month: {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: monthStart, to: monthEnd };
    }
    default:
      return { from: undefined, to: undefined };
  }
};

const DashboardHeader = ({
  onClear,
  dateRange = { from: undefined, to: undefined },
  onDateRangeChange,
  selectedEmail = "all",
  onEmailChange,
  datePreset = DATE_PRESETS.this_week,
  onDatePresetChange,
}) => {
  const [hierarchyData, setHierarchyData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHierarchy = async () => {
    setLoading(true);
    try {
      const result = await axiosInstance.get("/analytics/team/hierarchy/");
      if (result.data.status === "success") {
        setHierarchyData(result.data.data);
      } else {
        message.error("Failed to fetch hierarchy data");
      }
    } catch (error) {
      message.error("Error fetching hierarchy data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const handleDateChange = (dates) => {
    if (dates) {
      onDateRangeChange?.({ from: dates[0]?.toDate(), to: dates[1]?.toDate() });
    } else {
      onDateRangeChange?.({ from: undefined, to: undefined });
    }
  };

  const handlePresetChange = (preset) => {
    onDatePresetChange?.(preset);
    if (preset !== DATE_PRESETS.custom) {
      const newDateRange = getDateRangeFromPreset(preset);
      onDateRangeChange?.(newDateRange);
    }
  };

  // Build hierarchy options for the dropdown - only level 1 members
  const getHierarchyOptions = () => {
    const options = [];

    // Only get level 1 hierarchy members (direct children)
    if (hierarchyData && hierarchyData.hierarchy && Array.isArray(hierarchyData.hierarchy)) {
      hierarchyData.hierarchy.forEach((member) => {
        options.push({
          value: member.email || member.uid,
          label: member.user,
        });
      });
    }

    return options;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "16px",
        marginBottom: "24px",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <Select
          value={datePreset}
          style={{ width: 140 }}
          onChange={handlePresetChange}
          options={[
            { value: DATE_PRESETS.this_week, label: "This Week" },
            { value: DATE_PRESETS.today, label: "Today" },
            { value: DATE_PRESETS.this_month, label: "This Month" },
            { value: DATE_PRESETS.custom, label: "Custom" },
          ]}
        />

        {datePreset === DATE_PRESETS.custom && (
          <RangePicker
            style={{ width: 260 }}
            onChange={handleDateChange}
            placeholder={["Start Date", "End Date"]}
            value={
              dateRange.from && dateRange.to
                ? [dayjs(dateRange.from), dayjs(dateRange.to)]
                : null
            }
          />
        )}

        <Select
          defaultValue="all"
          style={{ width: 140 }}
          options={[
            { value: "all", label: "All Status" },
            { value: "completed", label: "Completed" },
            { value: "inprogress", label: "In Progress" },
            { value: "delayed", label: "Delayed" },
          ]}
        />

        <Select
          value={selectedEmail}
          style={{ width: 180 }}
          loading={loading}
          options={getHierarchyOptions()}
          placeholder="Select Team Member"
          onChange={onEmailChange}
        />

        <Button onClick={onClear}>Clear</Button>
      </div>
    </div>
  );
};

export default DashboardHeader;
